/* Shopify owns product, inventory, cart, pricing, search, and form state. */
(() => {
  'use strict';
  const root = document.body.dataset.rootUrl || '/';
  const url = path => `${root.endsWith('/') ? root : `${root}/`}${path}`;
  let cartBusy = false;
  let dialogTrigger = null;
  let toastTimer;
  let searchTimer;
  let searchController;

  const announce = message => {
    const toast = document.querySelector('.toast');
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('show'), 4500);
  };

  async function request(path, options = {}) {
    const requestUrl = new URL(path, window.location.href);
    if (requestUrl.origin !== window.location.origin || !['https:', 'http:'].includes(requestUrl.protocol)) {
      throw new Error('Store requests must stay on this site.');
    }
    const response = await fetch(path, { ...options, credentials: 'same-origin', mode: 'same-origin' });
    if (response.url && new URL(response.url, window.location.href).origin !== window.location.origin) {
      throw new Error('Store response came from an unexpected site.');
    }
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(typeof error.description === 'string' ? error.description : typeof error.message === 'string' ? error.message : 'Something went wrong. Please try again.');
    }
    return response;
  }

  function closeDialog(dialog) {
    if (!dialog?.open) return;
    dialog.close();
  }

  function bindDialog(dialog) {
    if (!dialog || dialog.dataset.bound) return;
    dialog.dataset.bound = 'true';
    dialog.addEventListener('close', () => {
      if (!document.querySelector('dialog[open]')) {
        document.body.classList.remove('dialog-open');
        if (dialogTrigger?.isConnected) dialogTrigger.focus();
      }
    });
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeDialog(dialog);
    });
  }

  function openDialog(dialog, trigger) {
    if (!dialog || typeof dialog.showModal !== 'function') return false;
    document.querySelectorAll('dialog[open]').forEach(other => { if (other !== dialog) other.close(); });
    if (trigger) dialogTrigger = trigger;
    bindDialog(dialog);
    if (!dialog.open) dialog.showModal();
    document.body.classList.add('dialog-open');
    if (dialog.id === 'SearchDialog') dialog.querySelector('input[type="search"]')?.focus();
    return true;
  }

  function replaceCart(html, open = false, trigger) {
    const parsed = new DOMParser().parseFromString(html || '', 'text/html');
    const next = parsed.querySelector('#CartDrawer');
    const current = document.querySelector('#CartDrawer');
    if (!next || !current) throw new Error('Your bag changed. Open the bag page to review it.');
    const count = next.dataset.cartCountValue;
    const wasOpen = current.open;
    // A replacement dialog must enter the top layer through showModal().
    next.removeAttribute('open');
    next.removeAttribute('data-bound');
    current.replaceWith(next);
    document.querySelectorAll('[data-cart-count]').forEach(node => { node.textContent = count; });
    document.querySelectorAll('[data-cart-open]').forEach(node => { node.setAttribute('aria-label', `Shopping bag, ${count} items`); });
    if (open || wasOpen) openDialog(next, trigger);
    return next;
  }

  async function refreshCart(open = false, trigger) {
    const response = await request(url('cart?section_id=cart-drawer'), { headers: { Accept: 'text/html' } });
    return replaceCart(await response.text(), open, trigger);
  }

  async function mutateCart(endpoint, data, errorNode, trigger, open) {
    if (cartBusy) return;
    cartBusy = true;
    const buttons = [...document.querySelectorAll('[data-product-form] button[type="submit"], [data-cart-form] button[type="submit"]')];
    const disabledStates = buttons.map(button => button.disabled);
    buttons.forEach(button => { button.disabled = true; });
    if (errorNode) errorNode.textContent = '';
    let changed = false;
    try {
      data.set('sections', 'cart-drawer');
      data.set('sections_url', window.location.pathname);
      const response = await request(url(endpoint), { method: 'POST', body: data, headers: { Accept: 'application/json' } });
      const result = await response.json();
      changed = true;
      if (result.sections?.['cart-drawer']) replaceCart(result.sections['cart-drawer'], open, trigger);
      else await refreshCart(open, trigger);
      if (endpoint === 'cart/add.js') announce('Added to your bag — thank you!');
    } catch (error) {
      if (changed) {
        // Never repeat an add when only the subsequent drawer render failed.
        window.location.assign(url('cart'));
        return;
      }
      if (errorNode) errorNode.textContent = error.message;
      announce(error.message);
    } finally {
      buttons.forEach((button, index) => { if (button.isConnected) button.disabled = disabledStates[index]; });
      cartBusy = false;
    }
  }

  async function loadAutomaticProducts(panel) {
    if (!panel.dataset.automaticProductsUrl || panel.dataset.productsLoaded || panel.dataset.productsLoading) return;
    const grid = panel.querySelector('[data-automatic-products-grid]');
    const status = panel.querySelector('[data-automatic-products-status]');
    if (!grid || !status) return;
    panel.dataset.productsLoading = 'true';
    grid.setAttribute('aria-busy', 'true');
    status.textContent = 'Loading products...';
    status.hidden = false;
    try {
      const feedUrl = new URL(panel.dataset.automaticProductsUrl, window.location.origin);
      feedUrl.searchParams.set('section_id', 'automatic-products');
      const limit = Math.min(12, Math.max(4, Number(panel.dataset.productLimit) || 4));
      const cards = [];
      const visited = new Set();
      let pageUrl = feedUrl;
      // Eligibility is checked by Liquid; keep scanning in the native sort order
      // when a catalog page has too few products with images and sufficient stock.
      while (pageUrl && cards.length < limit) {
        pageUrl.searchParams.set('section_id', 'automatic-products');
        if (visited.has(pageUrl.toString())) throw new Error('Product feed repeated a page.');
        visited.add(pageUrl.toString());
        const response = await request(pageUrl.toString());
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const feed = parsed.querySelector('[data-automatic-product-feed]');
        if (!feed || feed.dataset.sort !== feedUrl.searchParams.get('sort_by')) throw new Error('Product feed unavailable.');
        cards.push(...[...feed.children].filter(card => card.matches('.product')).slice(0, limit - cards.length));
        if (!feed.dataset.nextPageUrl || cards.length >= limit) break;
        const nextUrl = new URL(feed.dataset.nextPageUrl, pageUrl);
        if (nextUrl.origin !== feedUrl.origin || nextUrl.pathname !== feedUrl.pathname) throw new Error('Unexpected product feed page.');
        nextUrl.searchParams.set('sort_by', feedUrl.searchParams.get('sort_by'));
        pageUrl = nextUrl;
      }
      // Each tab can contain the same product; keep its form IDs unique.
      const ids = new Map();
      cards.forEach(card => card.querySelectorAll('[id]').forEach(node => {
        const previous = node.id;
        node.id = `${panel.id}-${previous}`;
        ids.set(previous, node.id);
      }));
      cards.forEach(card => card.querySelectorAll('[for], [aria-controls], [aria-labelledby], [aria-describedby]').forEach(node => {
        ['for', 'aria-controls', 'aria-labelledby', 'aria-describedby'].forEach(attribute => {
          if (node.hasAttribute(attribute)) node.setAttribute(attribute, node.getAttribute(attribute).split(' ').map(id => ids.get(id) || id).join(' '));
        });
      }));
      grid.replaceChildren(...cards);
      panel.dataset.productsLoaded = 'true';
      status.textContent = cards.length ? '' : 'New finds are on their way. Visit again soon.';
      status.hidden = cards.length > 0;
    } catch {
      status.textContent = 'Explore the collection using the link below.';
    } finally {
      delete panel.dataset.productsLoading;
      grid.removeAttribute('aria-busy');
    }
  }

  function activateTab(container, tab, focus = false) {
    container.querySelectorAll('[data-tab]').forEach(button => {
      const selected = button === tab;
      button.classList.toggle('active', selected);
      button.setAttribute('aria-selected', String(selected));
      button.tabIndex = selected ? 0 : -1;
      const panel = document.getElementById(button.getAttribute('aria-controls'));
      if (panel) {
        panel.hidden = !selected;
        if (selected) loadAutomaticProducts(panel);
      }
    });
    const collectionLink = container.querySelector('[data-active-collection-link]');
    if (collectionLink && tab.dataset.collectionUrl) {
      collectionLink.href = tab.dataset.collectionUrl;
      collectionLink.textContent = tab.dataset.viewAllLabel;
    }
    if (focus) tab.focus();
  }

  function showMedia(gallery, id) {
    gallery.querySelectorAll('[data-gallery-panel]').forEach(panel => {
      const selected = panel.id === id;
      if (!selected && !panel.hidden) {
        panel.querySelectorAll('video').forEach(video => video.pause());
        panel.querySelectorAll('iframe').forEach(frame => { frame.src = frame.src; });
      }
      panel.hidden = !selected;
    });
    gallery.querySelectorAll('[data-gallery-thumb]').forEach(button => {
      button.setAttribute('aria-pressed', String(button.getAttribute('aria-controls') === id));
    });
  }

  function initializeRoomVideos(scope = document) {
    scope.querySelectorAll('[data-room-video-toggle]').forEach(button => {
      const video = document.getElementById(button.getAttribute('aria-controls'));
      if (!video || video.dataset.toggleBound) return;
      video.dataset.toggleBound = 'true';
      video.controls = false;
      button.hidden = false;
      const sync = () => {
        const playing = !video.paused && !video.ended;
        button.classList.toggle('is-playing', playing);
        button.setAttribute('aria-label', playing ? 'Pause product tutorial' : 'Play product tutorial');
        button.setAttribute('aria-pressed', String(playing));
        button.querySelector('[data-video-play-icon]').hidden = playing;
        button.querySelector('[data-video-pause-icon]').hidden = !playing;
      };
      const toggle = async () => {
        if (!video.paused && !video.ended) video.pause();
        else {
          try { await video.play(); }
          catch { video.controls = true; button.hidden = true; }
        }
      };
      button.addEventListener('click', toggle);
      video.addEventListener('click', toggle);
      ['play', 'pause', 'ended'].forEach(type => video.addEventListener(type, sync));
      sync();
    });
  }

  function initializeAntlerPagination(scope = document) {
    scope.querySelectorAll('[data-antler-pagination]').forEach(section => {
      if (section.dataset.paginationBound) return;
      const grid = section.querySelector('.antler-products');
      if (!grid) return;
      section.dataset.paginationBound = 'true';
      const controls = section.querySelector('.antler-pagination');
      const previous = section.querySelector('[data-antler-prev]');
      const next = section.querySelector('[data-antler-next]');
      const status = section.querySelector('[data-antler-status]');
      const cards = [...grid.children];
      let nextBatch = section.dataset.nextBatch;
      let page = 0;
      let busy = false;
      grid.classList.add('is-paginated');
      const update = () => {
        cards.forEach((card, index) => { card.hidden = index < page * 6 || index >= (page + 1) * 6; });
        controls.hidden = cards.length <= 6 && !nextBatch;
        previous.disabled = busy || page === 0;
        next.disabled = busy || (!nextBatch && (page + 1) * 6 >= cards.length);
        status.textContent = cards.length ? `Page ${page + 1}` : '';
      };
      const changePage = async target => {
        if (busy || target < 0) return;
        busy = true;
        update();
        grid.setAttribute('aria-busy', 'true');
        try {
          // Fetch further collection batches only when the next six need them.
          while (nextBatch && cards.length < (target + 1) * 6) {
            const batchUrl = new URL(nextBatch, window.location.origin);
            batchUrl.searchParams.set('section_id', section.dataset.sectionId);
            const response = await request(batchUrl.toString());
            const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
            const batch = parsed.querySelector('[data-antler-pagination]');
            if (!batch) throw new Error('Could not load more products. Please try again.');
            const additions = [...batch.querySelectorAll('.antler-products > .product')];
            additions.forEach(card => { card.hidden = true; grid.append(card); cards.push(card); });
            nextBatch = batch.dataset.nextBatch;
          }
          if (target * 6 < cards.length) page = target;
        } catch (error) {
          announce(error.message);
          status.textContent = 'Could not load more products. Please try again.';
        } finally {
          const failed = status.textContent.startsWith('Could not');
          busy = false;
          grid.removeAttribute('aria-busy');
          update();
          if (failed) status.textContent = 'Could not load more products. Please try again.';
        }
      };
      previous.addEventListener('click', () => changePage(page - 1));
      next.addEventListener('click', () => changePage(page + 1));
      update();
      if (cards.length < 6 && nextBatch) changePage(0);
    });
  }

  function redirectEmptyCatalog(section) {
    if (!section.dataset.emptyRedirect) return false;
    try {
      const destination = new URL(section.dataset.emptyRedirect, window.location.origin);
      const current = new URL(window.location.href);
      if (destination.origin !== current.origin || !['http:', 'https:'].includes(destination.protocol)) return false;
      // A filtered Shop All URL can reset once to the unfiltered catalog, but
      // an empty unfiltered Shop All must never redirect back to itself.
      if (destination.pathname === current.pathname && destination.search === current.search) return false;
      window.location.replace(destination.toString());
      return true;
    } catch {
      return false;
    }
  }

  function initializeFilteredCatalogs(scope = document) {
    scope.querySelectorAll('[data-filtered-catalog]').forEach(section => {
      if (section.dataset.catalogBound) return;
      const grid = section.querySelector('[data-catalog-grid]');
      const status = section.querySelector('[data-catalog-status]');
      const controls = section.querySelector('[data-catalog-controls]');
      if (!grid || !status || !controls) return;
      section.dataset.catalogBound = 'true';
      section.classList.add('catalog-enhanced');
      const previous = section.querySelector('[data-catalog-prev]');
      const next = section.querySelector('[data-catalog-next]');
      const pageLabel = section.querySelector('[data-catalog-page]');
      const empty = section.querySelector('[data-catalog-empty]');
      const retry = section.querySelector('[data-catalog-retry]');
      const pageSize = 24;
      const cards = [];
      const keys = new Set();
      const visited = new Set();
      const sourceUrl = new URL(window.location.href);
      sourceUrl.searchParams.delete('catalog_page');
      sourceUrl.searchParams.delete('page');
      sourceUrl.searchParams.set('section_id', section.dataset.sectionId);
      let nextBatch = section.dataset.nextPage;
      let currentPage = 0;
      let pendingPage = 0;
      let busy = false;
      const requestedPage = () => {
        const params = new URL(window.location.href).searchParams;
        const value = Number(params.get('catalog_page') || params.get('page') || 1);
        return Number.isSafeInteger(value) && value > 0 ? Math.min(value, 1050) - 1 : 0;
      };
      const ingest = batch => {
        batch.querySelectorAll('[data-catalog-grid] > .product, [data-catalog-grid] > .search-result').forEach(card => {
          const key = card.querySelector('h3 a[href]')?.getAttribute('href');
          if (key && keys.has(key)) return;
          if (key) keys.add(key);
          cards.push(card);
        });
        nextBatch = batch.dataset.nextPage;
      };
      const update = () => {
        const start = currentPage * pageSize;
        const visible = cards.slice(start, start + pageSize);
        grid.replaceChildren(...visible);
        controls.hidden = cards.length <= pageSize && !nextBatch;
        previous.disabled = busy || currentPage === 0;
        next.disabled = busy || (!nextBatch && cards.length <= start + pageSize);
        pageLabel.textContent = `Page ${currentPage + 1}`;
        empty.hidden = busy || cards.length > 0;
        if (!busy) status.textContent = visible.length ? `Showing ${start + 1}\u2013${start + visible.length}` : '';
      };
      const loadBatch = async rawUrl => {
        const parsedUrl = new URL(rawUrl, window.location.href);
        if (parsedUrl.origin !== sourceUrl.origin || parsedUrl.pathname !== sourceUrl.pathname) throw new Error('Unexpected catalog page.');
        const sourcePage = Number(parsedUrl.searchParams.get('page') || 1);
        if (!Number.isSafeInteger(sourcePage) || sourcePage < 1 || visited.has(sourcePage)) throw new Error('Catalog page repeated.');
        // Only the page number comes from the continuation; preserve the active
        // search, filters and sort from this page for every section request.
        const batchUrl = new URL(sourceUrl);
        batchUrl.searchParams.set('page', String(sourcePage));
        const response = await request(batchUrl.toString());
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const batch = [...parsed.querySelectorAll('[data-filtered-catalog]')].find(node => node.dataset.sectionId === section.dataset.sectionId);
        if (!batch || Number(batch.dataset.sourcePage) !== sourcePage) throw new Error('Catalog page unavailable.');
        visited.add(sourcePage);
        ingest(batch);
      };
      const showPage = async (target, changeUrl = false, scroll = false) => {
        if (busy || target < 0) return;
        busy = true;
        pendingPage = target;
        retry.hidden = true;
        grid.setAttribute('aria-busy', 'true');
        update();
        status.textContent = 'Loading products...';
        let failed = false;
        let redirecting = false;
        try {
          // One extra card determines whether a real next page exists.
          while (nextBatch && cards.length < (target + 1) * pageSize + 1) await loadBatch(nextBatch);
          if (!cards.length && !nextBatch && redirectEmptyCatalog(section)) {
            redirecting = true;
            return;
          }
          currentPage = cards.length ? Math.min(target, Math.ceil(cards.length / pageSize) - 1) : 0;
          if (changeUrl || requestedPage() !== currentPage || new URL(window.location.href).searchParams.has('page')) {
            const locationUrl = new URL(window.location.href);
            locationUrl.searchParams.delete('page');
            if (currentPage) locationUrl.searchParams.set('catalog_page', String(currentPage + 1));
            else locationUrl.searchParams.delete('catalog_page');
            window.history[changeUrl ? 'pushState' : 'replaceState']({}, '', locationUrl);
          }
        } catch {
          failed = true;
        } finally {
          busy = false;
          grid.removeAttribute('aria-busy');
          update();
          if (redirecting) {
            empty.hidden = true;
            controls.hidden = true;
            status.textContent = '';
          } else if (failed) {
            empty.hidden = true;
            status.textContent = 'Could not load more products. Please try again.';
            retry.hidden = false;
          } else if (scroll) {
            status.setAttribute('tabindex', '-1');
            status.focus({ preventScroll: true });
            section.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
          }
        }
      };
      previous.addEventListener('click', () => showPage(currentPage - 1, true, true));
      next.addEventListener('click', () => showPage(currentPage + 1, true, true));
      retry.addEventListener('click', () => showPage(pendingPage));
      window.addEventListener('popstate', () => { if (section.isConnected) showPage(requestedPage()); });
      if (Number(section.dataset.sourcePage) === 1) {
        visited.add(1);
        ingest(section);
      } else {
        // Old native page links and filtered-page bookmarks start from the same
        // ordered source, so their display-page number remains meaningful.
        nextBatch = new URL(sourceUrl);
        nextBatch.searchParams.set('page', '1');
        nextBatch = nextBatch.toString();
      }
      showPage(requestedPage());
    });
  }

  function initialize(scope = document) {
    scope.querySelectorAll('[data-portfolio-dialog]').forEach(dialog => {
      if (dialog.dataset.portfolioReady) return;
      if (typeof dialog.showModal === 'function') {
        dialog.removeAttribute('open');
        dialog.dataset.portfolioReady = 'true';
      } else dialog.classList.add('portfolio-inline-fallback');
    });
    initializeFilteredCatalogs(scope);
    initializeAntlerPagination(scope);
    initializeRoomVideos(scope);
    scope.querySelectorAll('[data-gallery]').forEach(gallery => {
      const selected = gallery.querySelector('[data-selected-media]') || gallery.querySelector('[data-gallery-panel]');
      if (selected) showMedia(gallery, selected.id);
    });
    scope.querySelectorAll('[data-tabs]').forEach(container => {
      const tabs = container.querySelectorAll('[data-tab]');
      container.querySelector('.product-tabs')?.setAttribute('role', 'tablist');
      tabs.forEach(tab => tab.setAttribute('role', 'tab'));
      container.querySelectorAll('[data-tab-panel]').forEach(panel => panel.setAttribute('role', 'tabpanel'));
      if (tabs[0]) activateTab(container, tabs[0]);
    });
    scope.querySelectorAll('[data-variant-select]').forEach(select => { select.dataset.currentVariant = select.value; });
    scope.querySelectorAll('[data-recommendations-url]').forEach(async container => {
      if (container.dataset.loaded) return;
      container.dataset.loaded = 'true';
      try {
        const response = await request(container.dataset.recommendationsUrl);
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const next = parsed.querySelector('[data-recommendations-url]');
        if (next && container.isConnected) container.innerHTML = next.innerHTML;
      } catch { /* Related products are optional; shopping remains available. */ }
    });
    updateSticky();
  }

  function updateSticky() {
    document.querySelectorAll('[data-sticky-header]').forEach(header => header.classList.toggle('is-visible', window.scrollY > 180));
  }

  // Dependency-free component layer for Shopify's section-driven markup.
  class Component {
    constructor(element) { this.element = element; }
    mount() {}
  }
  class GalleryComponent extends Component {
    mount() { const selected = this.element.querySelector('[data-selected-media]') || this.element.querySelector('[data-gallery-panel]'); if (selected) showMedia(this.element, selected.id); }
  }
  class TabsComponent extends Component {
    mount() { const tabs = this.element.querySelectorAll('[data-tab]'); this.element.querySelector('.product-tabs')?.setAttribute('role', 'tablist'); tabs.forEach(tab => tab.setAttribute('role', 'tab')); this.element.querySelectorAll('[data-tab-panel]').forEach(panel => panel.setAttribute('role', 'tabpanel')); if (tabs[0]) activateTab(this.element, tabs[0]); }
  }
  class RecommendationsComponent extends Component {
    async mount() { if (this.element.dataset.loaded) return; this.element.dataset.loaded = 'true'; try { const response = await request(this.element.dataset.recommendationsUrl); const parsed = new DOMParser().parseFromString(await response.text(), 'text/html'); const next = parsed.querySelector('[data-recommendations-url]'); if (next && this.element.isConnected) this.element.replaceChildren(...next.childNodes); } catch { /* optional content */ } }
  }
  const componentTypes = [['[data-gallery]', GalleryComponent], ['[data-tabs]', TabsComponent], ['[data-recommendations-url]', RecommendationsComponent]];
  function mountComponents(scope = document) {
    componentTypes.forEach(([selector, Type]) => {
      const elements = scope.matches?.(selector) ? [scope] : scope.querySelectorAll(selector);
      elements.forEach(element => { if (element.__whiteAntlerComponent) return; const component = new Type(element); element.__whiteAntlerComponent = component; component.mount(); });
    });
  }

  function closeMenus(returnFocus = false) {
    document.querySelectorAll('.nav-dropdown[open]').forEach(menu => { menu.open = false; if (returnFocus) menu.querySelector('summary')?.focus(); });
    document.querySelectorAll('[data-menu-toggle][aria-expanded="true"]').forEach(button => {
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-label', 'Open navigation menu');
      document.getElementById(button.getAttribute('aria-controls'))?.classList.remove('open');
      if (returnFocus) button.focus();
    });
  }

  document.addEventListener('click', async event => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const thumbnail = target.closest('[data-gallery-thumb]');
    if (thumbnail) showMedia(thumbnail.closest('[data-gallery]'), thumbnail.getAttribute('aria-controls'));
    const quantityButton = target.closest('[data-quantity-step]');
    if (quantityButton) {
      const input = quantityButton.closest('.wa-quantity').querySelector('input[name="quantity"]');
      if (Number(quantityButton.dataset.quantityStep) > 0) input.stepUp(); else input.stepDown();
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    const variantChoice = target.closest('[data-variant-choice]');
    if (variantChoice && !variantChoice.disabled) {
      const select = variantChoice.closest('[data-product-section]').querySelector('[data-variant-select]');
      if (select && select.value !== variantChoice.dataset.variantChoice && !select.disabled) {
        select.value = variantChoice.dataset.variantChoice;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    const menuToggle = target.closest('[data-menu-toggle]');
    if (menuToggle) {
      const expanded = menuToggle.getAttribute('aria-expanded') !== 'true';
      menuToggle.setAttribute('aria-expanded', String(expanded));
      menuToggle.setAttribute('aria-label', expanded ? 'Close navigation menu' : 'Open navigation menu');
      document.getElementById(menuToggle.getAttribute('aria-controls'))?.classList.toggle('open', expanded);
    } else if (!target.closest('.header') || target.closest('.mobile-panel a')) closeMenus();
    document.querySelectorAll('.nav-dropdown[open]').forEach(menu => { if (!menu.contains(target)) menu.open = false; });

    if (target.closest('[data-close-dialog]')) closeDialog(target.closest('dialog'));
    const portfolio = target.closest('[data-portfolio-open]');
    if (portfolio && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
      if (openDialog(document.getElementById(portfolio.getAttribute('aria-controls')), portfolio)) event.preventDefault();
    }
    const search = target.closest('[data-search-open]');
    if (search && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
      if (openDialog(document.getElementById('SearchDialog'), search)) event.preventDefault();
    }
    const bag = target.closest('[data-cart-open]');
    if (bag && !event.ctrlKey && !event.metaKey && !event.shiftKey && !document.body.classList.contains('template-cart') && document.body.dataset.cartDrawer === 'true' && typeof HTMLDialogElement !== 'undefined') {
      event.preventDefault();
      if (cartBusy) return;
      try { await refreshCart(true, bag); } catch { window.location.assign(bag.href); }
    }
    const tab = target.closest('[data-tab]');
    if (tab) activateTab(tab.closest('[data-tabs]'), tab);
    const remove = target.closest('[data-remove-key]');
    if (remove?.closest('#CartDrawer')) {
      event.preventDefault();
      const data = new FormData();
      data.set('id', remove.dataset.removeKey);
      data.set('quantity', '0');
      await mutateCart('cart/change.js', data, remove.closest('form').querySelector('[data-cart-error]'), null, true);
    }
  });

  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') closeMenus(true);
    const tab = event.target.closest?.('[data-tab]');
    if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const container = tab.closest('[data-tabs]');
    const tabs = [...container.querySelectorAll('[data-tab]')];
    let next = tabs.indexOf(tab) + (event.key === 'ArrowLeft' ? -1 : 1);
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = tabs.length - 1;
    activateTab(container, tabs[(next + tabs.length) % tabs.length], true);
  });

  document.addEventListener('submit', async event => {
    const form = event.target;
    if (form.matches('[data-product-form]')) {
      event.preventDefault();
      const useDrawer = document.body.dataset.cartDrawer === 'true' && typeof HTMLDialogElement !== 'undefined';
      await mutateCart('cart/add.js', new FormData(form), form.querySelector('[data-form-error]'), event.submitter, useDrawer);
    } else if (form.matches('[data-cart-form]') && event.submitter?.name !== 'checkout') {
      event.preventDefault();
      await mutateCart('cart/update.js', new FormData(form), form.querySelector('[data-cart-error]'), null, true);
    }
  });

  document.addEventListener('change', async event => {
    const select = event.target;
    if (!select.matches('[data-variant-select]')) return;
    const section = select.closest('[data-product-section]');
    const variantId = select.value;
    const previousId = select.dataset.currentVariant;
    const requestedQuantity = Number(section.querySelector('input[name="quantity"]')?.value);
    const previousPlan = section.querySelector('[name="selling_plan"]')?.value;
    const controls = [...section.querySelectorAll('button, input, select')];
    const states = controls.map(control => control.disabled);
    controls.forEach(control => { control.disabled = true; });
    section.setAttribute('aria-busy', 'true');
    try {
      const productUrl = new URL(section.dataset.productUrl, window.location.origin);
      productUrl.searchParams.set('variant', variantId);
      productUrl.searchParams.set('section_id', section.dataset.productSection);
      const response = await request(productUrl.toString());
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const next = parsed.querySelector('[data-product-section]');
      if (!next) throw new Error('Could not load that option. Please try again.');
      section.replaceWith(next);
      initialize(next);
      const quantityInput = next.querySelector('input[name="quantity"]');
      if (quantityInput && Number.isFinite(requestedQuantity)) {
        const min = Number(quantityInput.min) || 1;
        const step = Number(quantityInput.step) || 1;
        const max = quantityInput.max ? Number(quantityInput.max) : Infinity;
        const clamped = Math.min(max, Math.max(min, requestedQuantity));
        quantityInput.value = String(min + Math.floor((clamped - min) / step) * step);
      }
      const planSelect = next.querySelector('[name="selling_plan"]');
      if (planSelect && [...planSelect.options].some(option => option.value === previousPlan)) planSelect.value = previousPlan;
      const nextSelect = next.querySelector('[data-variant-select]');
      if (nextSelect) nextSelect.dataset.currentVariant = nextSelect.value;
      const locationUrl = new URL(window.location.href);
      locationUrl.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', locationUrl);
      window.Shopify?.PaymentButton?.init();
      const nextChoice = next.querySelector('[data-variant-choice][aria-pressed="true"]');
      (nextChoice || nextSelect)?.focus();
      announce('Product option updated.');
    } catch (error) {
      select.value = previousId;
      controls.forEach((control, index) => { control.disabled = states[index]; });
      section.removeAttribute('aria-busy');
      const errorNode = section.querySelector('[data-form-error]');
      if (errorNode) errorNode.textContent = error.message;
    }
  });

  document.addEventListener('input', event => {
    if (!event.target.matches('[data-predictive-search]')) return;
    const input = event.target;
    const results = document.querySelector('[data-predictive-results]');
    clearTimeout(searchTimer);
    searchController?.abort();
    results.replaceChildren();
    const query = input.value.trim();
    if (query.length < 2) return;
    searchTimer = setTimeout(async () => {
      const controller = new AbortController();
      searchController = controller;
      results.setAttribute('aria-busy', 'true');
      try {
        const params = new URLSearchParams({ q: query, 'resources[type]': 'product,collection,article,page', 'resources[limit]': '4', section_id: 'predictive-search' });
        const response = await request(url(`search/suggest?${params}`), { signal: controller.signal });
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (input.value.trim() !== query) return;
        results.innerHTML = parsed.querySelector('.predictive-list')?.outerHTML || '<p>Press Search to see all results.</p>';
      } catch (error) {
        if (error.name !== 'AbortError') results.textContent = 'Press Search to see all results.';
      } finally {
        if (searchController === controller) results.removeAttribute('aria-busy');
      }
    }, 250);
  });

  window.addEventListener('scroll', updateSticky, { passive: true });
  window.addEventListener('pageshow', event => { if (event.persisted) refreshCart().catch(() => {}); });
  document.addEventListener('shopify:section:load', event => { initialize(event.target); mountComponents(event.target); });
  document.addEventListener('shopify:block:select', event => {
    const portfolioDialog = event.target.closest('[data-portfolio-dialog]');
    if (portfolioDialog) {
      const trigger = [...document.querySelectorAll('[data-portfolio-open]')].find(button => button.getAttribute('aria-controls') === portfolioDialog.id);
      openDialog(portfolioDialog, trigger);
    }
    const panel = event.target.closest('[data-tab-panel]');
    if (panel) {
      const tab = document.getElementById(panel.getAttribute('aria-labelledby'));
      if (tab) activateTab(panel.closest('[data-tabs]'), tab);
    }
  });
  initialize();
  mountComponents();
})();
